import { useSWRInfinite } from '@tiger/swr';
import { DatePicker, Form, Input, Skeleton, Space, Table, TableProps } from 'antd';
import lodash from 'lodash';
import { FC, FormEventHandler, useMemo, useRef, useState } from 'react';
import { useForm, withForm } from 'react-antd-formutil';
import { CaretDownOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Card from 'components/Card';
import Debug from 'components/Debug';
import EmptyData from 'components/EmptyData';
import ErrorBox from 'components/ErrorBox';
import FormItem from 'components/FormItem';
import LoadMore from 'components/LoadMore';
import { NAV_SYEP } from 'stores/menu';
import security from 'stores/security';
import API from 'utils/API';
import classlist from 'utils/classlist';
import { formatMoney } from 'utils/format';
import style from './style.scss';

interface DetailItem {
    currency: string; // 币种
    collateralAmount: number; // 抵押物价值
    interest: number; // 利息
    lendingDate: string; // 借出日期
    nameCN: string; // 名称
    quantity: number; // 数量
    stockId: number; // 股票ID
    symbol: string;
}

const DATE_FORMAT = 'YYYYMMDD';
const PAGE_LIMIT = 20;
const columns: TableProps<DetailItem>['columns'] = [
    {
        title: (
            <div>
                <div>{__('SYEP:Lent Symbol/')}</div>
                <div>{__('SYEP:Time')}</div>
            </div>
        ),
        dataIndex: 'symbol',
        align: 'left',
        render: (text, record) => (
            <div className={style.twoLineCell}>
                <div className={style.symbol}>{record.symbol}</div>
                <div className={style.lendingDate}>{dayjs(record.lendingDate).format('YYYY-MM-DD')}</div>
            </div>
        )
    },
    {
        title: __('Lent Qty'),
        dataIndex: 'quantity',
        align: 'right',
        render: quantity => <div className={style.quantity}>{quantity}</div>,
        className: style.lentQtyCell
    },
    {
        title: (
            <div>
                <div>{__('SYEP:Revenue/')}</div>
                <div>{__('SYEP:Cash Collateral')}</div>
            </div>
        ),
        dataIndex: 'interest',
        align: 'right',
        render: (interest, record) => (
            <div className={style.twoLineCell}>
                <div className={style.interest}>{interest}</div>
                <div className={style.collateralAmount}>
                    {formatMoney(record.collateralAmount)} {record.currency}
                </div>
            </div>
        )
    }
];

const Details: FC = () => {
    const [symbolPrefix, setSymbolPrefix] = useState<string>('');
    const containerRef = useRef<HTMLDivElement>(null);
    const { $params } = useForm<{
        since_date: dayjs.Dayjs | null;
        to_date: dayjs.Dayjs | null;
        symbol_prefix: string;
    }>();
    const { since_date, to_date } = $params;
    const { error, data, mutate, setSize, isLoading, isValidating } = useSWRInfinite<{
        details: {
            pageNum: number;
            pageSize: number;
            pageCount: number;
            items: DetailItem[];
        };
    }>(
        (page: number) =>
            since_date && to_date
                ? {
                      url: API.TRADE.reports.getSyepDetails(),
                      params: {
                          account_id: security.currentAccountId,
                          customer_id: security.currentCustomerId,
                          since_date, // 起始日期
                          to_date, // 截止日期
                          symbol_prefix: symbolPrefix, // symbol前缀
                          start: page * PAGE_LIMIT,
                          limit: PAGE_LIMIT // 分页参数, 默认为20
                      }
                  }
                : null,
        {
            dataChain: res => res.data
        }
    );
    const dataSource = useMemo(() => {
        // 把data中的items取出来, 拼接成一个数组，同时添加唯一ID
        return data
            ? data.flatMap((item, pageIndex) =>
                  item.details.items.map((record, itemIndex) => ({
                      ...record,
                      // 使用页码和行索引组合生成唯一ID
                      uniqueId: `page_${pageIndex}_item_${itemIndex}`
                  }))
              )
            : [];
    }, [data]);
    // 防止输入框输入触发接口请求过于频繁, 应该使用防抖, 而非节流
    const handleSearch: FormEventHandler<HTMLInputElement> = useMemo(
        () =>
            lodash.debounce(event => {
                const target = event.target as HTMLInputElement;

                setSymbolPrefix(target.value);
            }, 300),
        []
    );
    const isEnd = useMemo(() => {
        const lastPageLength = data?.lastItem.details.items.length || 0;

        return lastPageLength < PAGE_LIMIT;
    }, [data]);

    const renderRecords = () => {
        if (error) {
            return <ErrorBox error={error} onClick={() => mutate()} />;
        }

        if (isLoading) {
            return <Skeleton active />;
        }

        if (!data || dataSource.length === 0) {
            return <EmptyData />;
        }

        return (
            <div ref={containerRef}>
                <Table
                    rowKey="uniqueId"
                    className={style.tableContainer}
                    size="middle"
                    dataSource={dataSource}
                    columns={columns}
                    scroll={{ x: true }}
                    pagination={false}
                    components={{
                        header: {
                            cell: props => {
                                return (
                                    <th {...props} className={classlist(style.customerHeader, props.className || '')}>
                                        {props.children}
                                    </th>
                                );
                            }
                        }
                    }}
                />
                <LoadMore
                    isAuto
                    error={error}
                    loading={isValidating}
                    onScrollToBottom={() => {
                        setSize(s => s + 1);
                    }}
                    showMoreBtn
                    isEnd={isEnd}
                    container={containerRef.current!}
                />
            </div>
        );
    };

    return (
        <Card form className={style.root}>
            <h3 className={style.mainTitle}>{NAV_SYEP}</h3>
            <Form noValidate layout="vertical">
                <div className={style.header}>
                    <Space.Compact className={style.dateContainer}>
                        <FormItem
                            className={classlist(style.formItem, style.dateForm, style.sinceDateForm)}
                            name="since_date"
                            $defaultValue={dayjs().subtract(1, 'month').format(DATE_FORMAT)}
                            $parser={value => value && dayjs(value).format(DATE_FORMAT)}
                            $formatter={val => val && dayjs(val)}>
                            <DatePicker
                                allowClear={false}
                                className={style.date}
                                /**
                                 * 如果设置了 to_date，则 since_date 不能晚于 to_date
                                 */
                                disabledDate={current => {
                                    if ($params.to_date) {
                                        return current && current > dayjs($params.to_date).startOf('day');
                                    }

                                    return false;
                                }}
                                placeholder={__('起始日期')}
                                suffixIcon={<CaretDownOutlined />}
                            />
                        </FormItem>
                        <span className={style.separator}>{__('Date:to')}</span>
                        <FormItem
                            className={classlist(style.formItem, style.dateForm, style.toDateForm)}
                            name="to_date"
                            $defaultValue={dayjs().format(DATE_FORMAT)}
                            $parser={value => value && dayjs(value).format(DATE_FORMAT)}
                            $formatter={val => val && dayjs(val)}>
                            <DatePicker
                                allowClear={false}
                                className={style.date}
                                /**
                                 * 如果设置了 since_date，则 to_date 不能早于 since_date
                                 */
                                disabledDate={current => {
                                    if (since_date) {
                                        return current && current < dayjs(since_date).endOf('day');
                                    }

                                    return false;
                                }}
                                placeholder={__('截止日期')}
                                suffixIcon={<CaretDownOutlined />}
                            />
                        </FormItem>
                    </Space.Compact>
                    <div className={style.searchForm}>
                        <Input
                            className={style.search}
                            prefix={<SearchOutlined />}
                            placeholder={__('Syep:Symbol')}
                            onInput={handleSearch}
                        />
                    </div>
                </div>
                {renderRecords()}
            </Form>
            <Debug data={$params} />
        </Card>
    );
};

export default withForm(Details);
